import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Table } from '@ipc/ipc-components';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Scrollbar from 'perfect-scrollbar-react';
import classNames from 'classnames';
import 'perfect-scrollbar-react/dist/style.min.css';
import TableDefinitions from '../../TableDefinitions';
import downloadTableAsXlsx from '../excel/downloadTable';
import { tableFilteredDataSetSuccess, setTableActiveFilter } from '../actions/ListTableActions';
import { getActiveFilterByListId } from '../selectors/ListTableSelectors';

const paginationMapper = [];

const CustomTbodyComponent = (props) => {
  const {
    className,
    children,
  } = props;
  return (
    <div {...props} className={classNames('rt-tbody', className || [])}>
      <Scrollbar>{children}</Scrollbar>
    </div>
  );
};

CustomTbodyComponent.propTypes = {
  className: PropTypes.string,
  children: PropTypes.arrayOf(PropTypes.shape),
};

function ListTable(props) {
  const { activeFilter } = props;
  const [page, setPage] = useState(0);
  const visibleDataRef = useRef([]);

  const changePage = (pageIndex) => {
    setPage(pageIndex);
    paginationMapper[props.listId] = pageIndex;
    if (paginationMapper[props.listId] === null) {
      setPage(0);
      paginationMapper[props.listId] = pageIndex;
    } else {
      setPage(paginationMapper[props.listId]);
    }
  };

  const onPageSizeChange = () => {
    changePage(0);
  };

  const onFilteredChange = (newFiltered) => {
    changePage(0);
    if (visibleDataRef.current !== undefined) {
      // eslint-disable-next-line no-underscore-dangle
      const newVisibleData = visibleDataRef.current.map((index) => index._original);
      props.tableFilteredDataSetSuccess({ tableId: props.listId }, newVisibleData);
    } else {
      props.tableFilteredDataSetSuccess({ tableId: props.listId }, props.data);
    }
    props.setTableActiveFilter(props.listId, newFiltered);
  };

  const downloadTable = () => {
    const listId = TableDefinitions[props.listId].id;
    const downloadFileName = props.downloadFileAs ? props.downloadFileAs : listId;
    const templateFile = props.downloadFileTemplate ? props.downloadFileTemplate : listId;
    const additionalColumnGenerator = props.downloadAdditionalRowGenerator ? props.downloadAdditionalRowGenerator : undefined;

    const { data } = props;

    let dataSource = data;

    const visibleTableData = visibleDataRef.current;
    let { columns } = props.columns[0].columns.length !== 1 ? props.columns[0] : { Header: '', columns: props.columns.reduce((arr, elem) => [...arr, ...elem.columns], []) };

    // for additional column adding
    if (additionalColumnGenerator) {
      dataSource = [...data].reduce((accumulatedNewDataSource, row, index) => {
        const additionalColumnsData = additionalColumnGenerator(row, index);
        const newDataSource = {
          ...additionalColumnsData,
          ...row,
        };

        return [
          ...accumulatedNewDataSource,
          newDataSource,
        ];
      }, []);

      columns = columns.concat(props.columns[0].columnsOnDownload);
    }

    // collects row data if there is active sort or filter
    const downloadableSortedData = [];
    visibleTableData.forEach((row) => {
      dataSource.forEach((selectedRow) => {
        // eslint-disable-next-line no-underscore-dangle
        if (selectedRow === row._original) {
          downloadableSortedData.push(selectedRow);
        }
      });
    });

    downloadTableAsXlsx(visibleTableData !== props.data ? downloadableSortedData : dataSource, columns, templateFile, downloadFileName);
  };

  const {
    columns,
    data,
    getTrProps,
    getTdProps,
    pagination,
    loading,
    isDownloadEnable,
    listId,
  } = props;

  const hasColumns = !!(columns && columns.length);
  const isDownloadDisabled = () => data.length === 0;

  useEffect(() => {
    onFilteredChange([]);
  }, [listId]);

  return (
    <div className="list-wrapper">
      <section className="generated-list">
        {isDownloadEnable && (
          <div>
            <Button
              disabled={isDownloadDisabled()}
              className={`primary-button-${isDownloadDisabled()} download-action-buttons`}
              onClick={downloadTable}
            >
              DOWNLOAD
            </Button>
          </div>
        )}
        {hasColumns && !pagination && (
          <div className="generated-list__table-wrapper">
            <Table
              loading={loading}
              noDataText={loading ? '' : 'No results for the current selection.'}
              TbodyComponent={CustomTbodyComponent}
              data={data}
              columns={columns}
              pageSize={data.length}
              getTrProps={getTrProps}
              getTdProps={getTdProps}
              page={page}
              onPageChange={(pageIndex) => changePage(pageIndex)}
              onPageSizeChange={onPageSizeChange}
              showPagination={false}
              striped
              highlight
              className="-highlight"
              onFilteredChange={(newFiltered) => onFilteredChange(newFiltered)}
              filtered={activeFilter}
            >
              {(state, makeTable) => {
                visibleDataRef.current = [...state.sortedData];
                return makeTable();
              }}
            </Table>
          </div>
        )}
      </section>
    </div>
  );
}

ListTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.arrayOf(PropTypes.object),
  listId: PropTypes.string,
  getTrProps: PropTypes.func,
  getTdProps: PropTypes.func,
  loading: PropTypes.bool,
  pagination: PropTypes.bool,
  isDownloadEnable: PropTypes.bool,
  downloadFileAs: PropTypes.string,
  downloadFileTemplate: PropTypes.string,
  downloadAdditionalRowGenerator: PropTypes.func,
  tableFilteredDataSetSuccess: PropTypes.func,
  setTableActiveFilter: PropTypes.func,
  activeFilter: PropTypes.arrayOf(PropTypes.object),
};

ListTable.defaultProps = {
  isDownloadEnable: false,
};

const mapStateToProps = (state, props) => ({
  activeFilter: getActiveFilterByListId(state, props.listId),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  tableFilteredDataSetSuccess,
  setTableActiveFilter,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ListTable);
